import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Send, Mic, MicOff, ChevronDown, Wand2, User } from 'lucide-react';

const CHAT_CONFIG = {
  webhookUrl: 'https://hitanssanghrajka.app.n8n.cloud/webhook/chat-trigger-webhook/chat',
  route: 'general',
  timeoutMinutes: 15
};

const STORAGE_KEYS = {
  chatHistory: 'climb_chat_history',
  userInfo: 'climb_user_info',
  lastActivity: 'climb_last_activity',
  popupShown: 'chat_popup_shown_session',
  popupClosed: 'chat_popup_closed_session',
  chatId: 'chatId'
};

const countries = [
  { code: '+91', flag: '🇮🇳', name: 'India', placeholder: '9876543210' },
  { code: '+1', flag: '🇺🇸', name: 'United States', placeholder: '2345678901' },
  { code: '+1', flag: '🇨🇦', name: 'Canada', placeholder: '4161234567' },
  { code: '+44', flag: '🇬🇧', name: 'United Kingdom', placeholder: '7700123456' },
  { code: '+971', flag: '🇦🇪', name: 'UAE', placeholder: '501234567' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore', placeholder: '81234567' },
  { code: '+61', flag: '🇦🇺', name: 'Australia', placeholder: '412345678' },
  { code: '+49', flag: '🇩🇪', name: 'Germany', placeholder: '1512345678' },
  { code: '+33', flag: '🇫🇷', name: 'France', placeholder: '612345678' },
  { code: '+81', flag: '🇯🇵', name: 'Japan', placeholder: '9012345678' },
  { code: '+86', flag: '🇨🇳', name: 'China', placeholder: '13812345678' },
  { code: '+82', flag: '🇰🇷', name: 'South Korea', placeholder: '1012345678' },
  { code: '+55', flag: '🇧🇷', name: 'Brazil', placeholder: '11987654321' },
  { code: '+52', flag: '🇲🇽', name: 'Mexico', placeholder: '5512345678' },
  { code: '+27', flag: '🇿🇦', name: 'South Africa', placeholder: '821234567' },
  { code: '+234', flag: '🇳🇬', name: 'Nigeria', placeholder: '8012345678' },
  { code: '+254', flag: '🇰🇪', name: 'Kenya', placeholder: '712345678' },
  { code: '+20', flag: '🇪🇬', name: 'Egypt', placeholder: '1001234567' },
  { code: '+966', flag: '🇸🇦', name: 'Saudi Arabia', placeholder: '501234567' },
  { code: '+60', flag: '🇲🇾', name: 'Malaysia', placeholder: '123456789' },
  { code: '+62', flag: '🇮🇩', name: 'Indonesia', placeholder: '812345678' },
  { code: '+63', flag: '🇵🇭', name: 'Philippines', placeholder: '9171234567' },
  { code: '+66', flag: '🇹🇭', name: 'Thailand', placeholder: '812345678' },
  { code: '+84', flag: '🇻🇳', name: 'Vietnam', placeholder: '912345678' },
  { code: '+92', flag: '🇵🇰', name: 'Pakistan', placeholder: '3001234567' },
  { code: '+880', flag: '🇧🇩', name: 'Bangladesh', placeholder: '1712345678' },
  { code: '+94', flag: '🇱🇰', name: 'Sri Lanka', placeholder: '712345678' },
  { code: '+977', flag: '🇳🇵', name: 'Nepal', placeholder: '9841234567' },
];

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showWelcomePopup, setShowWelcomePopup] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [pendingMessage, setPendingMessage] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', phone: '' });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(countries[0]);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Generate or get chat ID
  const getChatId = useCallback(() => {
    let chatId = sessionStorage.getItem(STORAGE_KEYS.chatId);
    if (!chatId) {
      chatId = 'chat_' + Math.random().toString(36).slice(2, 11);
      sessionStorage.setItem(STORAGE_KEYS.chatId, chatId);
    }
    return chatId;
  }, []);

  // Save chat history
  const saveChatHistory = useCallback((msgs) => {
    try {
      localStorage.setItem(STORAGE_KEYS.chatHistory, JSON.stringify(msgs));
      localStorage.setItem(STORAGE_KEYS.lastActivity, Date.now().toString());
    } catch (e) {
      console.warn('Failed to save chat history:', e);
    }
  }, []);

  // Clear chat data
  const clearChatData = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEYS.chatHistory);
      localStorage.removeItem(STORAGE_KEYS.userInfo);
      localStorage.removeItem(STORAGE_KEYS.lastActivity);
      sessionStorage.removeItem(STORAGE_KEYS.chatId);
    } catch (e) {
      console.warn('Failed to clear chat data:', e);
    }
  }, []);

  // Load chat history
  const loadChatHistory = useCallback(() => {
    try {
      const lastActivity = localStorage.getItem(STORAGE_KEYS.lastActivity);
      if (lastActivity) {
        const timeDiff = Date.now() - parseInt(lastActivity);
        const timeoutMs = CHAT_CONFIG.timeoutMinutes * 60 * 1000;
        if (timeDiff > timeoutMs) {
          clearChatData();
          return { messages: [], userInfo: null };
        }
      }

      const savedHistory = localStorage.getItem(STORAGE_KEYS.chatHistory);
      const savedUserInfo = localStorage.getItem(STORAGE_KEYS.userInfo);

      return {
        messages: savedHistory ? JSON.parse(savedHistory) : [],
        userInfo: savedUserInfo ? JSON.parse(savedUserInfo) : null
      };
    } catch (e) {
      console.warn('Failed to load chat history:', e);
      return { messages: [], userInfo: null };
    }
  }, [clearChatData]);

  // Reset timeout
  const resetTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    localStorage.setItem(STORAGE_KEYS.lastActivity, Date.now().toString());
    
    timeoutRef.current = setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: 'Your chat session has expired due to inactivity. Your conversation history has been cleared.',
        isUser: false,
        timestamp: Date.now()
      }]);
      setTimeout(() => {
        clearChatData();
        setUserInfo(null);
        setMessages([]);
        setShowUserForm(false);
      }, 3000);
    }, CHAT_CONFIG.timeoutMinutes * 60 * 1000);
  }, [clearChatData]);

  // Initialize
  useEffect(() => {
    const { messages: savedMessages, userInfo: savedUserInfo } = loadChatHistory();
    if (savedMessages.length > 0) {
      setMessages(savedMessages);
    }
    if (savedUserInfo) {
      setUserInfo(savedUserInfo);
    }

    // Show welcome popup after delay if not closed before
    const popupClosed = sessionStorage.getItem(STORAGE_KEYS.popupClosed);
    if (!popupClosed) {
      setTimeout(() => {
        setShowWelcomePopup(true);
      }, 2000);
    }

    // Initialize speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let transcript = '';
        for (let i = event.resultIndex; i < event.results.length; i++) {
          transcript += event.results[i][0].transcript;
        }
        setInputMessage(transcript);
      };

      recognitionRef.current.onerror = () => {
        setIsRecording(false);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [loadChatHistory]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Save messages when they change
  useEffect(() => {
    if (messages.length > 0) {
      saveChatHistory(messages);
    }
  }, [messages, saveChatHistory]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleVoice = () => {
    if (!recognitionRef.current) return;
    
    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (e) {
        console.error('Failed to start voice:', e);
      }
    }
  };

  const sendMessageToAI = async (message) => {
    setIsTyping(true);
    resetTimeout();

    try {
      const res = await fetch(CHAT_CONFIG.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: getChatId(),
          message: message,
          route: CHAT_CONFIG.route,
          userInfo: userInfo
        })
      });

      let data;
      try {
        data = await res.json();
      } catch {
        data = { output: await res.text() };
      }

      const botOutput = data?.output || data?.message || data?.text || "Sorry, I couldn't understand that.";
      
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: botOutput,
        isUser: false,
        timestamp: Date.now()
      }]);
    } catch (err) {
      console.error('Chat webhook error', err);
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: 'Sorry, there was an error sending your message.',
        isUser: false,
        timestamp: Date.now()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSendMessage = async () => {
    const message = inputMessage.trim();
    if (!message) return;

    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    }

    setInputMessage('');

    if (!userInfo) {
      setPendingMessage(message);
      setMessages(prev => [...prev, {
        id: Date.now(),
        text: message,
        isUser: true,
        timestamp: Date.now()
      }]);
      setShowUserForm(true);
      return;
    }

    setMessages(prev => [...prev, {
      id: Date.now(),
      text: message,
      isUser: true,
      timestamp: Date.now()
    }]);

    await sendMessageToAI(message);
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Please enter your full name';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Please enter your phone number';
    } else if (formData.phone.length < 6) {
      errors.phone = 'Phone number is too short';
    } else if (!/^[0-9+\-\s()]+$/.test(formData.phone)) {
      errors.phone = 'Phone number contains invalid characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const fullPhoneNumber = selectedCountry.code + formData.phone;
    const userInfoData = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: fullPhoneNumber
    };

    try {
      const response = await fetch(CHAT_CONFIG.webhookUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chatId: getChatId(),
          action: 'user_info',
          userInfo: userInfoData,
          route: CHAT_CONFIG.route
        })
      });

      if (response.ok) {
        setUserInfo(userInfoData);
        localStorage.setItem(STORAGE_KEYS.userInfo, JSON.stringify(userInfoData));
        setShowUserForm(false);
        setFormData({ name: '', email: '', phone: '' });
        
        if (pendingMessage) {
          await sendMessageToAI(pendingMessage);
          setPendingMessage('');
        }
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Error submitting user info:', error);
      setFormErrors({ submit: 'Failed to submit information. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openChat = () => {
    setIsOpen(true);
    setShowWelcomePopup(false);
    sessionStorage.setItem(STORAGE_KEYS.popupClosed, '1');
    resetTimeout();
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const closeChat = () => {
    setIsOpen(false);
    if (isRecording) {
      recognitionRef.current?.stop();
      setIsRecording(false);
    }
  };

  const dismissPopup = () => {
    setShowWelcomePopup(false);
    sessionStorage.setItem(STORAGE_KEYS.popupClosed, '1');
  };

  // Simple markdown-like rendering (bold, links, line breaks)
  const renderMarkdown = (text) => {
    try {
      let html = text
        // Escape HTML first
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        // Bold **text**
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Italic *text*
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Links [text](url)
        .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        // Line breaks
        .replace(/\n/g, '<br/>');
      return { __html: html };
    } catch {
      return { __html: text };
    }
  };

  return (
    <>
      {/* Welcome Popup */}
      {showWelcomePopup && !isOpen && (
        <div 
          className="fixed bottom-[100px] right-5 z-[9999] animate-in slide-in-from-bottom-2 duration-300"
          style={{ animation: 'popupIn 0.45s cubic-bezier(.2,.9,.3,1) both' }}
        >
          <div 
            className="max-w-[280px] bg-gradient-to-b from-white to-slate-50 rounded-[14px] p-4 shadow-[0_12px_30px_rgba(20,24,58,0.12)] border border-[rgba(27,45,74,0.06)] cursor-pointer relative"
            onClick={openChat}
          >
            <p className="text-sm text-slate-900 font-bold leading-relaxed">
              Hi 👋 Looking to grow your wealth? Our AI financial expert are here to help. Start a chat.
            </p>
            <button
              onClick={(e) => { e.stopPropagation(); dismissPopup(); }}
              className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-white shadow-md flex items-center justify-center text-gray-500 hover:text-gray-700 border border-gray-100"
            >
              <X size={14} />
            </button>
            {/* Triangle pointer */}
            <div className="absolute -bottom-2 right-6 w-4 h-4 rotate-45 bg-gradient-to-br from-white to-slate-50 border-r border-b border-[rgba(27,45,74,0.06)]" />
          </div>
        </div>
      )}

      {/* Chat Button */}
      <button
        onClick={() => isOpen ? closeChat() : openChat()}
        className="fixed bottom-5 right-5 w-16 h-16 rounded-full bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600 text-white text-2xl cursor-pointer shadow-[0_12px_40px_rgba(250,175,0,0.3),0_4px_16px_rgba(250,175,0,0.2)] transition-all duration-300 flex items-center justify-center z-[10000] hover:scale-105"
        style={{ animation: isOpen ? 'none' : 'pulse 3s ease-in-out infinite' }}
      >
        {isOpen ? (
          <X size={24} />
        ) : (
          <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            <circle cx="8" cy="10" r="1.5"/>
            <circle cx="12" cy="10" r="1.5"/>
            <circle cx="16" cy="10" r="1.5"/>
          </svg>
        )}
      </button>

      {/* Chat Widget Container */}
      {isOpen && (
        <div 
          className="fixed right-5 bottom-[100px] w-[420px] max-w-[calc(100vw-32px)] h-[580px] max-h-[calc(100vh-140px)] bg-white/95 backdrop-blur-xl rounded-[20px] border border-yellow-400/20 shadow-[0_20px_60px_rgba(250,175,0,0.12)] flex flex-col overflow-hidden z-[9998]"
          style={{ animation: 'slideInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1)' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-yellow-400 to-yellow-500 text-white px-5 py-4 flex items-center justify-between">
            <span className="font-semibold text-base tracking-tight">Support Chat</span>
            <button 
              onClick={closeChat}
              className="bg-white/15 p-2 rounded-lg hover:bg-white/25 transition-all min-w-[44px] min-h-[44px] flex items-center justify-center"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages Body */}
          <div className="flex-1 min-h-0 p-5 overflow-y-auto bg-white flex flex-col gap-2">
            {/* Welcome Message */}
            {messages.length === 0 && !showUserForm && (
              <div className="text-center my-4 select-none">
                <span className="block font-semibold text-[17px] mb-2 text-[#1b2d4a] tracking-tight">
                  Welcome to CLIMB CAPITAL SERVICES PVT LTD!
                </span>
                <span className="block text-gray-500 text-sm max-w-[300px] mx-auto">
                  I'm Orion, Climb Capital's AI assistant. Feel free to ask me anything.
                </span>
              </div>
            )}

            {/* Messages */}
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex items-start gap-3 my-2 ${message.isUser ? 'flex-row-reverse ml-auto' : 'flex-row mr-auto'}`}
              >
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5 shadow-md ${
                    message.isUser 
                      ? 'bg-gradient-to-br from-gray-500 to-gray-600 text-white' 
                      : 'bg-[#1b2d4a] text-white'
                  }`}
                >
                  {message.isUser ? <User size={14} /> : <Wand2 size={14} />}
                </div>
                <div
                  className={`px-4 py-3 rounded-[20px] text-[15px] leading-relaxed max-w-[230px] shadow-[0_2px_12px_rgba(0,0,0,0.08)] break-words ${
                    message.isUser
                      ? 'bg-gradient-to-br from-slate-50 to-slate-100 text-gray-800 border border-black/5 font-medium'
                      : 'bg-gradient-to-br from-white to-slate-50 text-gray-800 border border-black/5 rounded-bl-lg text-sm'
                  }`}
                >
                  {message.isUser ? (
                    message.text
                  ) : (
                    <div 
                      className="prose prose-sm max-w-none [&_a]:text-[#1b2d4a] [&_a]:underline [&_a]:font-medium [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:my-2 [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:pl-5 [&_li]:my-1 [&_strong]:font-semibold [&_strong]:text-slate-900 [&_code]:bg-[#1b2d4a]/10 [&_code]:text-[#1b2d4a] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[13px]"
                      dangerouslySetInnerHTML={renderMarkdown(message.text)} 
                    />
                  )}
                </div>
              </div>
            ))}

            {/* User Info Form */}
            {showUserForm && (
              <div className="flex items-start gap-3 my-2 mr-auto">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5 shadow-md bg-[#1b2d4a] text-white">
                  <Wand2 size={14} />
                </div>
                <div className="bg-gradient-to-br from-white to-slate-50 border border-black/5 rounded-[20px] rounded-bl-lg p-4 max-w-[280px] shadow-[0_2px_12px_rgba(0,0,0,0.08)]">
                  <h3 className="text-[#1b2d4a] font-semibold text-[15px] mb-1">Let's get to know you better!</h3>
                  <p className="text-gray-500 text-xs mb-4">Provide your info so we can reach you after you've left</p>
                  
                  <form onSubmit={handleFormSubmit} className="space-y-3">
                    <div>
                      <label className="block text-gray-600 text-[11px] font-medium mb-1">Full Name *</label>
                      <input
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="Enter your name"
                        className={`w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:border-[#1b2d4a] ${formErrors.name ? 'border-red-500' : 'border-gray-200'}`}
                      />
                      {formErrors.name && <p className="text-red-500 text-[9px] mt-0.5">{formErrors.name}</p>}
                    </div>

                    <div>
                      <label className="block text-gray-600 text-[11px] font-medium mb-1">Email Address *</label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        placeholder="Enter your email"
                        className={`w-full px-2 py-1.5 border rounded-md text-sm focus:outline-none focus:border-[#1b2d4a] ${formErrors.email ? 'border-red-500' : 'border-gray-200'}`}
                      />
                      {formErrors.email && <p className="text-red-500 text-[9px] mt-0.5">{formErrors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-gray-600 text-[11px] font-medium mb-1">Phone Number *</label>
                      <div className={`flex border rounded-md overflow-hidden ${formErrors.phone ? 'border-red-500' : 'border-gray-200'} focus-within:border-[#1b2d4a]`}>
                        <div className="relative" ref={dropdownRef}>
                          <button
                            type="button"
                            onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                            className="flex items-center gap-1 px-2 py-1.5 bg-gray-50 border-r border-gray-200 hover:bg-gray-100 text-[10px] min-w-[70px] justify-center"
                          >
                            <span className="text-sm">{selectedCountry.flag}</span>
                            <span className="font-medium">{selectedCountry.code}</span>
                            <ChevronDown size={10} />
                          </button>
                          
                          {showCountryDropdown && (
                            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-[180px] overflow-y-auto z-50 min-w-[200px]">
                              {countries.map((country, idx) => (
                                <div
                                  key={`${country.code}-${country.name}-${idx}`}
                                  onClick={() => {
                                    setSelectedCountry(country);
                                    setShowCountryDropdown(false);
                                  }}
                                  className={`flex items-center gap-2 px-2.5 py-2 cursor-pointer text-[11px] hover:bg-gray-100 border-b border-gray-50 last:border-0 ${
                                    selectedCountry.code === country.code && selectedCountry.name === country.name
                                      ? 'bg-[#1b2d4a] text-white hover:bg-[#1b2d4a]'
                                      : ''
                                  }`}
                                >
                                  <span className="text-sm">{country.flag}</span>
                                  <span className="flex-1">{country.name}</span>
                                  <span className="font-medium">{country.code}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder={selectedCountry.placeholder}
                          className="flex-1 px-2 py-1.5 text-sm focus:outline-none"
                        />
                      </div>
                      {formErrors.phone && <p className="text-red-500 text-[9px] mt-0.5">{formErrors.phone}</p>}
                    </div>

                    {formErrors.submit && (
                      <p className="text-red-500 text-[9px] text-center">{formErrors.submit}</p>
                    )}

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full py-2 bg-[#1b2d4a] text-white rounded-md text-[11px] font-semibold hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex items-start gap-3 my-2 mr-auto">
                <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm flex-shrink-0 mt-0.5 shadow-md bg-[#1b2d4a] text-white">
                  <Wand2 size={14} />
                </div>
                <div className="flex items-center gap-3 text-gray-500">
                  <span 
                    className="text-base font-medium bg-gradient-to-r from-gray-500 via-gray-400 to-gray-500 bg-clip-text text-transparent bg-[length:200%_100%]"
                    style={{ animation: 'thinkingShimmer 2s ease-in-out infinite' }}
                  >
                    Orion is typing
                  </span>
                  <span className="flex gap-2">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="w-2 h-2 rounded-full bg-[#1b2d4a] opacity-70"
                        style={{ 
                          animation: 'thinkingDots 1.4s infinite ease-in-out',
                          animationDelay: `${i * 0.2}s`
                        }}
                      />
                    ))}
                  </span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Footer Input */}
          <div className="px-5 py-4 border-t border-gray-200 bg-white">
            <div className="flex items-center bg-gray-50 border border-gray-200 rounded-[25px]">
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Message..."
                className="flex-1 px-4 py-3 bg-transparent border-none outline-none text-base"
              />
              {recognitionRef.current && (
                <button
                  onClick={toggleVoice}
                  className={`p-3 rounded-full transition-colors ${isRecording ? 'bg-red-100 text-red-500' : 'text-gray-500 hover:text-gray-700'}`}
                >
                  {isRecording ? <MicOff size={18} /> : <Mic size={18} />}
                </button>
              )}
              <button
                onClick={handleSendMessage}
                className={`p-3 rounded-full transition-colors ${inputMessage.trim() ? 'text-[#1b2d4a]' : 'text-gray-400'}`}
              >
                <Send size={18} />
              </button>
            </div>
          </div>

          {/* Powered By */}
          <div className="text-center text-[11px] text-gray-500 pb-3 bg-white border-t border-gray-100">
            Powered by{' '}
            <a 
              href="https://www.zymralabs.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#1b2d4a] font-semibold hover:underline"
            >
              ZYMRA LABS
            </a>
          </div>
        </div>
      )}

      {/* CSS Keyframes */}
      <style>{`
        @keyframes slideInUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes popupIn {
          from { opacity: 0; transform: translateY(6px) scale(.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes thinkingShimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        @keyframes thinkingDots {
          0%, 80%, 100% { transform: scale(0.8) translateY(0); opacity: 0.4; }
          40% { transform: scale(1.2) translateY(-6px); opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ChatWidget;
